import { Button, Input, LabeledLine } from "@touche-lts/panel-ui/dist";
import React, { useState } from "react";
import { ValueTagEditor } from "../../../../components/common/value-tag-editor";
import { AddShippingDTO } from "../../../../models/shipping";
import styles from "../add-store-page.module.scss";
import shippingEditStyles from "./shipping-edit.module.scss";
import { CheckboxButton } from "../checkbox-button";
import { InputSelect } from "../input-select";
import { RadioButton } from "../radio-button";

const shippingChronoUnitsAsSelectOptions = [
  { label: "Hours", value: "HOURS" },
  { label: "Days", value: "DAYS" },
  { label: "Weeks", value: "WEEKS" },
  { label: "Months", value: "MONTHS" },
];

interface ShippingEditProps {
  value?: AddShippingDTO;
  onSave?: (shipping: AddShippingDTO) => void;
  onEdit?: (shipping: AddShippingDTO) => void;
  onCancel: () => void;
}

const ShippingEdit: React.FC<ShippingEditProps> = (props) => {
  const { value, onSave, onEdit, onCancel } = props;

  const [shipping, setShipping] = useState<AddShippingDTO>({
    name: value?.name || "",
    freeShipping: value?.freeShipping || false,
    freeShippingAbove: value?.freeShippingAbove || false,
    deliveryTime: value?.deliveryTime || "",
    chronoUnit: value?.chronoUnit || "DAYS",
    currency: value?.currency || "USD",
    price: value?.price || 0,
    freeAmount: value?.freeAmount || 0,
    countries: value?.countries || [],
  });

  const [validationError, setValidationError] = useState<string>("");

  return (
    <div>
      <LabeledLine className={styles.inputLine} label="Shipping Name">
        <Input
          className={styles.input}
          placeholder="Shipping name..."
          style={{ width: 300 }}
          value={shipping.name}
          onChange={(name) => setShipping({ ...shipping, name })}
        />
      </LabeledLine>
      <LabeledLine className={styles.inputLine} label="Delivery time">
        <InputSelect
          className={styles.input}
          inputProps={{
            placeholder: "Delivery time...",
            value: shipping.deliveryTime,
            onChange: (deliveryTime: string) =>
              setShipping({ ...shipping, deliveryTime }),
          }}
          selectProps={{
            options: shippingChronoUnitsAsSelectOptions,
            noNoneOption: true,
            value: shipping.chronoUnit,
            onChange: (chronoUnit: string) =>
              setShipping({ ...shipping, chronoUnit }),
          }}
        />
      </LabeledLine>

      <LabeledLine
        className={styles.inputLine}
        label="Price"
        alignItems="stretch"
      >
        <div className={shippingEditStyles.priceOptions}>
          <div className={shippingEditStyles.priceRow}>
            <div className={shippingEditStyles.priceOption}>
              <RadioButton
                selected={!shipping.freeShipping}
                onClick={() =>
                  setShipping({ ...shipping, freeShipping: false })
                }
              />
              <div style={{ display: "flex", flexDirection: "column" }}>
                <InputSelect
                  className={styles.input}
                  style={{ marginLeft: 5, width: 240 }}
                  inputProps={{
                    placeholder: "Price...",
                    value: shipping.price,
                    onChange: (price: string) =>
                      setShipping({ ...shipping, price: Number(price) }),
                  }}
                  selectProps={{
                    options: [{ label: "USD", value: "USD" }],
                    value: "USD",
                    noNoneOption: true,
                  }}
                />
              </div>
            </div>
            <div className={shippingEditStyles.checkboxOption}>
              <CheckboxButton
                className="w-[30px]"
                checkboxProps={{
                  isChecked: shipping.freeShippingAbove,
                }}
                label="Free shipping above"
                onClick={() =>
                  setShipping({
                    ...shipping,
                    freeShippingAbove: !shipping.freeShippingAbove,
                  })
                }
              />
              <InputSelect
                className={styles.input}
                style={{ marginLeft: 10, width: 240 }}
                inputProps={{
                  placeholder: "Amount...",
                  value: shipping.freeAmount,
                  onChange: (freeAmount: string) =>
                    setShipping({
                      ...shipping,
                      freeAmount: Number(freeAmount),
                    }),
                }}
                selectProps={{
                  options: [{ label: "USD", value: "USD" }],
                  value: "USD",
                  noNoneOption: true,
                }}
              />
            </div>
          </div>
          <RadioButton
            className={styles.marginTop}
            label="Free shipping"
            selected={shipping.freeShipping}
            onClick={() => setShipping({ ...shipping, freeShipping: true })}
          />
        </div>
      </LabeledLine>
      <LabeledLine
        className={styles.inputLine}
        label="Ships to"
        alignItems="stretch"
      >
        <ValueTagEditor
          editMode
          style={{ width: "100%" }}
          valueTags={shipping.countries.map((c) => ({
            valueName: c,
            onRemove: () =>
              setShipping({
                ...shipping,
                countries: shipping.countries.filter(
                  (country) => country !== c
                ),
              }),
          }))}
          onInputValueAdded={(country: string) => {
            const countryAlreadyAdded = shipping.countries.includes(country);
            if (countryAlreadyAdded) return;
            return setShipping({
              ...shipping,
              countries: [...shipping.countries, country],
            });
          }}
        />
      </LabeledLine>

      {validationError && (
        <div className={shippingEditStyles.validationError}>
          {validationError}
        </div>
      )}

      <div className={shippingEditStyles.buttonContainer}>
        <Button preset="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          preset="primary"
          onClick={() => {
            // Validate required fields
            if (!shipping.name.trim()) {
              setValidationError("Shipping name is required");
              return;
            }
            if (!shipping.deliveryTime.trim()) {
              setValidationError("Delivery time is required");
              return;
            }
            if (!shipping.freeShipping && shipping.price <= 0) {
              setValidationError("Price is required");
              return;
            }
            if (shipping.countries.length === 0) {
              setValidationError("At least one country is required");
              return;
            }
            setValidationError("");

            if (value && onEdit) return onEdit(shipping);
            if (onSave) return onSave(shipping);
          }}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

export default ShippingEdit;
